import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, ArrowRight, Navigation, ExternalLink, Sparkles, Clock, Star, Quote } from "lucide-react";
import Navbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import { api, LOGO_URL, INSTAGRAM_URL, PHONE, PHONE_TEL, ADDRESS_LINE, ADDRESS_CITY, MAPS_QUERY, euro } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (src) => (src.startsWith("http") ? src : `${BACKEND_URL}${src}`);

const CATEGORY_ORDER = ["Gelnagels", "Bijwerking – eigen werk", "Verwijdering", "Nail art"];

export default function PublicSite() {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setTestimonials(data)).catch(() => {});
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.getElementById(window.location.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: services.filter((s) => s.category === cat),
  })).filter((g) => g.items.length);

  return (
    <div className="bg-white">
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-brand-blush">
        <div className="absolute -top-24 -right-24 w-[32rem] h-[32rem] rounded-full bg-brand-pink/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-brand-pink/15 blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-6 pt-28 pb-16 w-full text-center flex flex-col items-center">
          <img src={LOGO_URL} alt="Nails Renss logo" className="h-24 w-24 rounded-full shadow-sm animate-float mb-6" />
          <span className="eyebrow animate-fade-up">Nagelstudio · Mater</span>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl font-semibold tracking-tight text-brand-charcoal leading-[0.95] mt-4 animate-fade-up" style={{ animationDelay: "0.05s" }}>
            Nails Renss
          </h1>
          <p className="font-display text-3xl sm:text-4xl text-brand-pinkhover mt-3 italic animate-fade-up" style={{ animationDelay: "0.12s" }}>
            Jouw nagels, jouw stijl.
          </p>
          <div className="flex items-center gap-3 my-6 animate-fade-up" style={{ animationDelay: "0.18s" }}>
            <span className="h-px w-10 bg-brand-pink/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-brand-pink" />
            <span className="h-px w-10 bg-brand-pink/60" />
          </div>
          <p className="text-brand-charcoal/70 text-base sm:text-lg max-w-md leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
            Ontdek verzorgde en stijlvolle nagels, volledig afgestemd op jouw look.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-8 animate-fade-up" style={{ animationDelay: "0.28s" }}>
            <Link to="/boeken" className="group inline-flex items-center justify-center gap-2 bg-brand-charcoal text-white font-semibold px-8 py-4 rounded-full hover:bg-brand-pinkhover transition-colors" data-testid="hero-book-now-button">
              BOEK JE AFSPRAAK <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="#prijslijst" className="inline-flex items-center justify-center gap-2 bg-white border border-brand-border text-brand-charcoal font-semibold px-8 py-4 rounded-full hover:border-brand-pink transition-colors" data-testid="hero-view-prices-button">
              BEKIJK PRIJZEN
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mt-9 text-sm animate-fade-up" style={{ animationDelay: "0.36s" }}>
            <span className="flex items-center gap-2 text-brand-charcoal/70"><MapPin size={16} className="text-brand-pink" /> {ADDRESS_LINE} · {ADDRESS_CITY}</span>
            <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 text-brand-charcoal/70 hover:text-brand-pinkhover"><Phone size={16} className="text-brand-pink" /> {PHONE}</a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-charcoal/70 hover:text-brand-pinkhover"><Instagram size={16} className="text-brand-pink" /> @nails.renss</a>
          </div>
        </div>
      </section>

      {/* PRIJSLIJST */}
      <section id="prijslijst" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="eyebrow mb-3">Prijslijst</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-brand-charcoal">Behandelingen & prijzen</h2>
            <p className="text-brand-muted mt-4 max-w-md mx-auto text-[15px]">Transparante prijzen voor elke behandeling. Boek eenvoudig online wat bij jou past.</p>
          </div>

          <div className="space-y-12">
            {grouped.map((g) => (
              <div key={g.cat} data-testid={`price-category-${g.cat}`}>
                <div className="flex items-center gap-4 mb-5">
                  <h3 className="font-display text-2xl font-medium text-brand-charcoal whitespace-nowrap">{g.cat}</h3>
                  <span className="h-px flex-1 bg-brand-border" />
                </div>
                <div className="space-y-2">
                  {g.items.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-4 py-3.5 px-4 sm:px-5 rounded-2xl hover:bg-brand-blush transition-colors group">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-brand-charcoal">{s.name}</p>
                          {s.duration_minutes ? (
                            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-brand-muted"><Clock size={12} /> {s.duration_minutes} min</span>
                          ) : null}
                        </div>
                        {s.description ? <p className="text-sm text-brand-muted mt-0.5 truncate">{s.description}</p> : null}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="font-display text-2xl font-semibold text-brand-charcoal">{euro(s.price)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/boeken" className="inline-flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-8 py-4 rounded-full hover:bg-brand-pinkhover transition-colors" data-testid="prices-book-button">
              BOEK JE AFSPRAAK <ArrowRight size={18} />
            </Link>
            <p className="text-xs text-brand-muted mt-4">Betaling gebeurt ter plaatse. Er is geen voorschot vereist.</p>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="py-20 md:py-28 bg-brand-neutral">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="eyebrow mb-3 flex items-center justify-center gap-2"><Sparkles size={14} /> Instagram</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-brand-charcoal">Volg @nails.renss</h2>
          <p className="text-brand-muted mt-4 max-w-md mx-auto text-[15px]">Laat je inspireren door onze laatste creaties en nail art.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-10">
            {gallery.map((g) => (
              <a key={g.id} href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="relative aspect-square rounded-2xl overflow-hidden group" data-testid={`gallery-item-${g.id}`}>
                <img src={resolveSrc(g.src)} alt={g.caption || "Nagelcreatie door Nails Renss"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-brand-charcoal/0 group-hover:bg-brand-charcoal/30 transition-colors flex items-center justify-center">
                  <Instagram className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                </div>
              </a>
            ))}
          </div>

          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-10 bg-brand-pink text-brand-charcoal font-semibold px-8 py-4 rounded-full hover:bg-brand-pinkhover hover:text-white transition-colors" data-testid="instagram-follow-button">
            <Instagram size={18} /> Volg Nails.Renss op Instagram
          </a>
        </div>
      </section>

      {/* ERVARINGEN / TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section id="ervaringen" className="py-20 md:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="eyebrow mb-3">Ervaringen</p>
              <h2 className="font-display text-4xl sm:text-5xl font-semibold text-brand-charcoal">Wat klanten zeggen</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.slice(0, 6).map((t, i) => (
                <div key={i} className="bg-brand-neutral rounded-3xl border border-brand-border p-6" data-testid={`testimonial-${i}`}>
                  <Quote className="text-brand-pink mb-3" size={22} />
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={15} className={s <= t.rating ? "fill-brand-pink text-brand-pink" : "text-brand-border"} />)}
                  </div>
                  {t.text && <p className="text-brand-charcoal/85 text-[15px] leading-relaxed">“{t.text}”</p>}
                  <p className="mt-4 text-sm font-medium text-brand-charcoal">— {t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LOCATIE */}
      <section id="locatie" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-3">Locatie</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold text-brand-charcoal mb-5">Waar vind je ons?</h2>
            <div className="space-y-1 text-brand-charcoal/80 text-lg">
              <p className="font-medium">Nails Renss</p>
              <p>{ADDRESS_LINE}</p>
              <p>{ADDRESS_CITY}</p>
              <p>België</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href={`https://www.google.com/maps?q=${MAPS_QUERY}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-charcoal text-white font-semibold px-6 py-3.5 rounded-full hover:bg-brand-pinkhover transition-colors" data-testid="open-maps-button">
                <ExternalLink size={17} /> Open in Google Maps
              </a>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-white border border-brand-border text-brand-charcoal font-semibold px-6 py-3.5 rounded-full hover:border-brand-pink transition-colors" data-testid="route-button">
                <Navigation size={17} /> Route plannen
              </a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-brand-border shadow-sm h-[340px] md:h-[420px]">
            <iframe
              title="Kaart Nails Renss"
              src={`https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`}
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 md:py-28 bg-brand-blush">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="eyebrow mb-3">Contact</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-brand-charcoal mb-4">Klaar voor mooie nagels?</h2>
          <p className="text-brand-muted max-w-md mx-auto text-[15px] mb-10">Boek je afspraak online of neem contact op. We helpen je graag verder.</p>

          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <a href={`tel:${PHONE_TEL}`} className="bg-white rounded-2xl border border-brand-border p-6 hover:border-brand-pink transition-colors">
              <Phone className="mx-auto text-brand-pink mb-3" size={24} />
              <p className="text-xs text-brand-muted uppercase tracking-wide">Telefoon</p>
              <p className="font-medium text-brand-charcoal mt-1">{PHONE}</p>
            </a>
            <div className="bg-white rounded-2xl border border-brand-border p-6">
              <MapPin className="mx-auto text-brand-pink mb-3" size={24} />
              <p className="text-xs text-brand-muted uppercase tracking-wide">Adres</p>
              <p className="font-medium text-brand-charcoal mt-1 text-sm">{ADDRESS_LINE}<br />{ADDRESS_CITY}</p>
            </div>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="bg-white rounded-2xl border border-brand-border p-6 hover:border-brand-pink transition-colors">
              <Instagram className="mx-auto text-brand-pink mb-3" size={24} />
              <p className="text-xs text-brand-muted uppercase tracking-wide">Instagram</p>
              <p className="font-medium text-brand-charcoal mt-1">@nails.renss</p>
            </a>
          </div>

          <Link to="/boeken" className="inline-flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-10 py-4 rounded-full hover:bg-brand-pinkhover transition-colors" data-testid="contact-book-button">
            BOEK JE AFSPRAAK <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
